import * as React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Header from '../layouts/Header';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Link from '@material-ui/core/Link';

export interface PrivacyPolicyViewProps {

}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            // height: '100vh'
        },
        card: {
            width: '100%',
            marginTop: 32,
            textAlign: 'justify'
        },
        pos: {
            marginBottom: 12,
        },
        italic: {
            fontSize: 'italic'
        }
    })
);

const PrivacyPolicyView: React.StatelessComponent<PrivacyPolicyViewProps> = props => {
    const classes = useStyles({});

    return (
        <div className={classes.root}>
            <Header />
            <CssBaseline />
            <Container maxWidth="lg">
                <Card className={classes.card}>
                    <CardContent>
                        <Typography variant="h4">
                            Privacy Policy
                        </Typography>
                        <Typography className={classes.pos} color="textSecondary">
                            Last updated September 09, 2020
                        </Typography>
                        <Typography paragraph gutterBottom>
                            Thank you for choosing to be part of our community at Leixir Resources Private Limited (“Company”, “we”, “us”, or “our”).
                            We are committed to protecting your personal information and your right to privacy.
                            If you have any questions or concerns about this privacy policy, or our practices with regards to your personal information, please contact us at sagar.grover@leixir.com.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            In the course of using this website <Link href="https://designconnect.mylabconnect.com">https://designconnect.mylabconnect.com</Link>
                            (the "Website"), mobile applications, and more generally,
                            use any of our services (the "Services", which include the Website or Mobile application),
                            we appreciate that you are trusting us with your personal information. We take your privacy very seriously
                        </Typography>
                        <Typography paragraph gutterBottom>
                            It is recommended that the Customers necessarily choose their passwords carefully such that no unauthorized access is made by a third party.
                            To make the password complex and difficult for others to guess, the Customers should use combination of alphabets, numbers and special characters (like !, @, #, $ etc.).
                            The Customers should undertake not to disclose their password to anyone or keep any written or other record of the password such that a third party could access it.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            In this privacy policy, we seek to explain to you in the clearest way possible what information we collect,
                            how we use it and what rights you have in relation to it. We hope you take some time to read through it carefully, as it is important.
                            If there are any terms in this privacy policy that you do not agree with, please discontinue use of our Services immediately.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            This privacy policy applies to all information collected through our Services (which, as described above, includes our Website and Mobile application),
                            as well as any related services, sales, marketing or events.
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Please read this privacy policy carefully as it will help you understand what we do with the information that we collect.
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                            1. WHAT INFORMATION DO WE COLLECT?
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Personal information you disclose to us
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  We collect information that you provide to us.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We collect personal information that you voluntarily provide to us when you register on the Website or Mobile application,
                            express an interest in obtaining information about us or our products and Services, when you participate in activities on the Website or otherwise when you contact us.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            The personal information that we collect depends on the context of your interactions with us and the Website, the choices you make and the products and features you use.
                            The personal information we collect may include the following:
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Personal Information Provided by You
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We collect names, phone numbers, email addresses, mailing addresses, usernames, passwords, billing addresses, debit/credit card numbers and other similar information.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            All personal information that you provide to us must be true, complete and accurate, and you must notify us of any changes to such personal information.
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Information automatically collected
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong>In Short:</strong>  Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically
                        when you visit our Website.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We automatically collect certain information when you visit, use or navigate the Website. This information does not reveal your specific identity
                            (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system,
                            language preferences, referring URLs, device name, country, location, information about who and when you use our Website and other technical information.
                            This information is primarily needed to maintain the security and operation of our Website, and for our internal analytics and reporting purposes.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            Like many businesses, we also collect information through cookies and similar technologies
                        </Typography>
                        <Typography paragraph gutterBottom>
                            The information we collect includes:
                        </Typography>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Log and Usage Data. Log and usage data is service-related, diagnostic usage and performance information our servers automatically collect when you access or
                                    use our Website and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type
                                    and settings and information about your activity in the Website (such as the date/time stamps associated with your usage, pages and files viewed, searches and
                                    other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called 'crash dumps') and
                                    hardware settings).
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    Device Data. We collect device data such as information about your computer, phone, tablet or other device you use to access the Website
                                    . Depending on the device used,this device data may include information such as your IP address (or proxy server),
                                    device application identification numbers, location, browser type, hardware modelInternet service provider and/or mobile carrier,
                                    operating system configuration information.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    Location Data. We collect information data such as information about your device's location, which can be either precise or imprecise.
                                    How much information we collect depends on the type of settings of the device you use to access the Website. For example, we may use GPS
                                    and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect
                                    this information either by refusing access to the information or by disabling your Locations settings on your device. Note however, if you choose to opt out,
                                    you may not be able to use certain aspects of the Services.
                                </Typography>
                            </li>
                        </ul>
                        <Typography variant="h5" gutterBottom>
                            2. HOW DO WE USE YOUR INFORMATION?
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  We process your information for purposes based on legitimate business interests, the fulfillment of our contract with you,
                            compliance with our legal obligations, and/or your consent.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We use personal information collected via our Website or Mobile application for a variety of business purposes described below. We process your personal
                            information for these purposes in reliance on our legitimate business interests, in order to enter into or perform a contract with you, with your consent,
                            and/or for compliance with our legal obligations. We indicate the specific processing grounds we rely on next to each purpose listed below.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We use the information we collect or receive:
                        </Typography>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Request feedback. We may use your information to request feedback and to contact you about your use of our Website.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To manage user accounts. We may use your information for the purposes of managing our account and keeping it in working order.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To send administrative information to you. We may use your personal information to send you product, service and new feature information
                                    and/or information about changes to our terms, conditions, and policies.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To protect our Services. We may use your information as part of our efforts to keep our Website safe and secure (for example, for fraud monitoring and prevention).
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To enforce our terms, conditions and policies for business purposes, to comply with legal and regulatory requirements or in connection with our contract.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To respond to legal requests and prevent harm. If we receive a subpoena or other legal request, we may need to inspect the data we hold to determine how to respond.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    Fulfill and manage your orders. We may use your information to fulfill and manage your orders, payments, returns, and exchanges made through the Website.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To deliver and facilitate delivery of services to the user. We may use your information to provide you with the requested service.
                                </Typography>
                            </li>
                            <li>
                                <Typography paragraph>
                                    To respond to user inquiries/offer support to users. We may use your information to respond to your inquiries and
                                    solve any potential issues you might have with the use of our Services.
                                </Typography>
                            </li>
                        </ul>
                        <Typography variant="h5" gutterBottom>
                            3.	WILL YOUR INFORMATION BE SHARED WITH ANYONE?
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  We only share information with your consent, to comply with laws, to provide you with services,
                            to protect your rights, or to fulfill business obligations.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We may process or share your data that we hold based on the following legal basis:
                        </Typography>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Consent: We may process your data if you have given us specific consent to use your personal information in a specific purpose.
                                </Typography>
                            </li>
                        </ul>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Legitimate Interests: We may process your data when it is reasonably necessary to achieve our legitimate business interests.
                                </Typography>
                            </li>
                        </ul>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Performance of a Contract: Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract.
                                </Typography>
                            </li>
                        </ul>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Legal Obligations: We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests,
                                    a judicial proceeding, court order, or legal process, such as in response to a court order or a subpoena (including in response to public authorities to meet
                                    national security or law enforcement requirements).
                                </Typography>
                            </li>
                        </ul>
                        <ul>
                            <li>
                                <Typography paragraph>
                                    Vital Interests: We may disclose your information where we believe it is necessary to investigate, prevent, or take action regarding potential violations
                                    of our policies, suspected fraud, situations involving potential threats to the safety of any person and illegal activities, or as evidence in litigation
                                    in which we are involved.
                                </Typography>
                            </li>
                        </ul>
                        <Typography variant="h5" gutterBottom>
                            4.	HOW LONG DO WE KEEP YOUR INFORMATION?
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  We keep your information for as long as necessary to fulfill the purposes outlined in this privacy policy unless otherwise required by law.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or
                            permitted by law (such as tax, accounting or other legal requirements). No purpose in this policy will require us keeping your personal information for longer
                            than one hundred twenty (120) months past the start of idle period of the user's account.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible
                            (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further
                            processing until deletion is possible.
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                            5.	HOW DO WE KEEP YOUR INFORMATION SAFE?
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  We aim to protect your personal information through a system of organizational and technical security measures.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process.
                            However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology
                            can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able
                            to defeat our security, and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information,
                            transmission of personal information to and from our Website is at your own risk. You should only access the Website and Mobile application within a secure environment.
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                            6.	DO WE MAKE UPDATES TO THIS POLICY?
                        </Typography>
                        <Typography variant="subtitle1" className={classes.italic} gutterBottom>
                            <strong> In Short:</strong>  Yes, we will update this policy as necessary to stay compliant with relevant laws.
                        </Typography>
                        <Typography paragraph gutterBottom>
                            We may update this privacy policy from time to time. The updated version will be indicated by an updated “Revised” date and the updated version will be effective as soon as
                            it is accessible. If we make material changes to this privacy policy, we may notify you either by prominently posting a policy of such changes or by directly
                            sending you a notification. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                            7.	HOW CAN YOU CONTACT US ABOUT THIS POLICY?
                        </Typography>
                        <Typography paragraph gutterBottom>
                            If you have questions or comments about this policy, you may email us at sagar.grover@leixir.com or by post to:
                        </Typography>
                        <Typography>
                            <strong>Leixir Resources Private Limited,</strong>
                        </Typography>
                        <Typography>
                            <strong>Ground Floor Office Plot No. D 12/1 and 14, Block -D,</strong>
                        </Typography>
                        <Typography>
                            <strong>Infocity -2, Sector -33,</strong>
                        </Typography>
                        <Typography>
                            <strong>Gurgaon, Haryana 122001,</strong>
                        </Typography>
                        <Typography gutterBottom>
                            <strong>India.</strong>
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                            8.	HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                        </Typography>
                        <Typography paragraph gutterBottom>
                            Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you,
                            change that information, or delete it in some circumstances. To request to review, update, or delete your personal information,
                            please visit: <Link href="https://designconnect.mylabconnect.com">https://designconnect.mylabconnect.com</Link> . We will respond to your request within 30 days.
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </div >
    );
};

export default PrivacyPolicyView;



// WEBPACK FOOTER //
// ./src/components/layouts/PrivacyPolicyView.tsx